// src/common/theme/components.js
import { alpha } from '@mui/material/styles';

export default {
  // ------------------------------------------------------------------
  // Inputs base (TextField/OutlinedInput etc.) — ajustado p/ padrão Traccar
  // ------------------------------------------------------------------
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
        transition: 'background-color .2s ease',
        minHeight: 40,
        '&:focus-within': {
          backgroundColor: theme.palette.background.paper,
          boxShadow: 'none',
        },
      }),
      input: {
        padding: '8px 10px',
        fontSize: '0.9rem',
      },
    },
  },

  // ------------------------------------------------------------------
  // TextField — mantém proporção e contraste
  // ------------------------------------------------------------------
  MuiTextField: {
    defaultProps: {
      fullWidth: true,
      variant: 'outlined',
      size: 'small',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiOutlinedInput-root': {
          borderRadius: 4,
          '& fieldset': { borderColor: theme.palette.divider },
          '&:hover fieldset': { borderColor: theme.palette.primary.main },
          '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
        },
      }),
    },
  },

  // ------------------------------------------------------------------
  // OutlinedInput — igual ao padrão MUI do Traccar
  // ------------------------------------------------------------------
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 4,
        minHeight: 40,
        backgroundColor: theme.palette.background.paper,
        '& fieldset': { borderColor: theme.palette.divider },
        '&:hover fieldset': { borderColor: theme.palette.primary.main },
        '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
      }),
      input: { padding: '8px 10px' },
    },
  },

  // ------------------------------------------------------------------
  // Select / Autocomplete — compactos e fiéis ao layout original
  // ------------------------------------------------------------------
  MuiSelect: {
    defaultProps: { size: 'small', fullWidth: true },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
        minHeight: 40,
        '& .MuiSelect-select': {
          padding: '8px 10px',
        },
      }),
    },
  },
  MuiAutocomplete: {
    defaultProps: { size: 'small', fullWidth: true },
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiOutlinedInput-root': {
          borderRadius: 4,
          minHeight: 40,
          '& fieldset': { borderColor: theme.palette.divider },
          '&:hover fieldset': { borderColor: theme.palette.primary.main },
          '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
        },
      }),
    },
  },

  // ------------------------------------------------------------------
  // Buttons — mantidos modernos, export personalizados
  // ------------------------------------------------------------------
  MuiButton: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const exp = (theme.palette && theme.palette.export) || {};
        const get = (k, fb) => exp[k] || fb;

        if (ownerState?.color === 'csv') {
          return {
            backgroundColor: get('csv', '#F97316'),
            color: theme.palette.common.white,
            fontWeight: 600,
            borderRadius: 6,
            minHeight: 40,
            textTransform: 'none',
            '&:hover': { backgroundColor: get('csvHover', '#FB923C') },
          };
        }
        if (ownerState?.color === 'excel') {
          return {
            backgroundColor: get('excel', '#009688'),
            color: theme.palette.common.white,
            fontWeight: 600,
            borderRadius: 6,
            minHeight: 40,
            textTransform: 'none',
            '&:hover': { backgroundColor: get('excelHover', '#00796B') },
          };
        }
        if (ownerState?.color === 'pdf') {
          return {
            backgroundColor: get('pdf', '#E53935'),
            color: theme.palette.common.white,
            fontWeight: 600,
            borderRadius: 6,
            minHeight: 40,
            textTransform: 'none',
            '&:hover': { backgroundColor: get('pdfHover', '#C62828') },
          };
        }
        return {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 6,
          transition: 'all .2s ease',
          padding: '6px 16px',
          minHeight: 40,
          '&.Mui-disabled': {
            opacity: 0.65,
            boxShadow: 'none',
          },
        };
      },
      containedPrimary: {
        boxShadow: '0 3px 6px rgba(0,0,0,0.12)',
        '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 5px 8px rgba(0,0,0,0.18)' },
        transition: 'all .2s ease',
      },
    },
  },

  // ------------------------------------------------------------------
  // Feedback e auxiliares
  // ------------------------------------------------------------------
  MuiFormControl: {
    defaultProps: { size: 'small' },
  },
  MuiSnackbar: {
    defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'center' } },
  },
  MuiTooltip: {
    defaultProps: { enterDelay: 500, enterNextDelay: 500 },
  },

  // ------------------------------------------------------------------
  // Layout e containers
  // ------------------------------------------------------------------
  MuiPaper: {
    styleOverrides: { root: { borderRadius: 12 } },
  },
  MuiCard: {
    styleOverrides: {
      root: { borderRadius: 12, boxShadow: '0 6px 12px rgba(0,0,0,0.1)' },
    },
  },
  MuiDialog: {
    styleOverrides: { paper: { borderRadius: 12 } },
  },
  MuiAppBar: {
    styleOverrides: { root: { borderRadius: 0 } },
  },

  // ------------------------------------------------------------------
  // Tabelas — cabeçalho e hover
  // ------------------------------------------------------------------
  MuiTableCell: {
    styleOverrides: {
      head: ({ theme }) => ({
        backgroundColor: theme.palette.custom?.table?.headerBg || '#f5f5f5',
        borderBottom: `1px solid ${
          theme.palette.custom?.table?.headerBorder || theme.palette.divider
        }`,
        color: theme.palette.custom?.table?.headerText || theme.palette.text.primary,
        fontWeight: 600,
      }),
      root: ({ theme }) => ({
        '@media print': {
          color: theme.palette.alwaysDark?.main || '#000',
        },
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:hover': {
          backgroundColor:
            theme.palette.custom?.table?.rowHover ||
            (theme.palette.mode === 'dark' ? '#2A2A2A' : '#FAFAFA'),
        },
      }),
    },
  },

  MuiTablePagination: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        '.MuiTablePagination-toolbar': {
          minHeight: '48px',
          paddingLeft: 16,
          paddingRight: 8,
        },
        '.MuiTablePagination-select': {
          width: 60,
          minWidth: 60,
          fontSize: '0.875rem',
          borderRadius: 4,
          paddingTop: 2,
          paddingBottom: 2,
        },
        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
          fontSize: '0.875rem',
          margin: 0,
        },
        '.MuiTablePagination-actions .MuiIconButton-root': { padding: 4 },
      }),
    },
  },

  // ------------------------------------------------------------------
  // Accordions
  // ------------------------------------------------------------------
  MuiAccordion: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(2),
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : alpha(theme.palette.primary.light, 0.3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: 48,
        '& .MuiAccordionSummary-content': {
          alignItems: 'center',
          gap: theme.spacing(1),
          margin: theme.spacing(0.5, 0),
        },
      }),
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: ({ theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
      }),
    },
  },

  // ------------------------------------------------------------------
  // Utilitários
  // ------------------------------------------------------------------
  MuiUseMediaQuery: {
    defaultProps: { noSsr: true },
  },
};
